// src\pages\GalleryUpload.js

import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/GalleryUpload.css";

const MAX_PHOTOS = 20;

const GalleryUpload = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [schoolHashtag, setSchoolHashtag] = useState(""); // Replace schoolName
  const [displayName, setDisplayName] = useState("");
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!token) return;
      try {
        const response = await axios.get("https://ghrianghraffy.com/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSchoolHashtag(response.data.school_hashtag || response.data.school_name.replace(/\s+/g, "") || ""); // Fallback to school_name if unset
        setDisplayName(response.data.display_name || "");
      } catch (err) {
        console.error("Failed to fetch profile data:", err);
      }
    };
    fetchProfileData();
  }, [token]);

  const handlePickPhotos = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newPhotos = selectedFiles.slice(0, MAX_PHOTOS - photos.length).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      story: "",
    }));
    setPhotos((prev) => [...prev, ...newPhotos].slice(0, MAX_PHOTOS));
    setError("");
    fileInputRef.current.value = "";
  };

  const handleDeletePhoto = (preview) => {
    setPhotos((prev) => {
      const updated = prev.filter((photo) => photo.preview !== preview);
      URL.revokeObjectURL(preview);
      return updated;
    });
  };

  const handleStoryChange = (preview, value) => {
    setPhotos((prev) =>
      prev.map((photo) =>
        photo.preview === preview ? { ...photo, story: value } : photo
      )
    );
  };

  const canUpload =
    photos.length > 0 && photos.every((photo) => photo.story.trim().length > 0);

  const handleUpload = async () => {
    if (!canUpload) {
      setError("Please add a story for each photo.");
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setError("");

    if (!token) {
      setError("You are not authorized. Please log in.");
      navigate("/login");
      return;
    }

    try {
      const totalPhotos = photos.length;
      let uploadedPhotos = 0;

      for (const photo of photos) {
        const formData = new FormData();
        formData.append("files", photo.file);
        const cleanedDisplayName = displayName.replace(/[\s.]+/g, "");
        const storyWithHashtags = `${photo.story.trim()} ${schoolHashtag} #${cleanedDisplayName}`;
        formData.append("stories", storyWithHashtags);

        await axios.post("https://ghrianghraffy.com/photos/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            setProgress(
              ((uploadedPhotos + percentCompleted / 100) / totalPhotos) * 100
            );
          },
        });

        uploadedPhotos++;
        setProgress((uploadedPhotos / totalPhotos) * 100);
      }

      setPhotos([]);
      setTimeout(() => navigate("/view-photos"), 1000);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || "Failed to upload photos.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="gallery-upload-container">
      <h1>Upload Photos</h1>
      <p>Select up to {MAX_PHOTOS} photos from your device, add stories/hashtags, and upload.</p>

      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        onChange={handlePickPhotos}
        disabled={photos.length >= MAX_PHOTOS || isUploading}
        className="file-input"
      />

      {photos.length > 0 && (
        <div className="photo-list">
          {photos.map((photo) => (
            <div key={photo.preview} className="photo-item">
              <img src={photo.preview} alt="Preview" className="photo-preview" />
              <button
                onClick={() => handleDeletePhoto(photo.preview)}
                className="delete-button"
                disabled={isUploading}
              >
                X
              </button>
              <textarea
                value={photo.story}
                onChange={(e) => handleStoryChange(photo.preview, e.target.value)}
                placeholder="Add a story & hashtags"
                className="story-input"
                disabled={isUploading}
              />
            </div>
          ))}
        </div>
      )}

      <p className="photo-count">Selected: {photos.length}/{MAX_PHOTOS}</p>

      {photos.length > 0 && (
        <button
          onClick={handleUpload}
          disabled={!canUpload || isUploading}
          className="upload-button"
        >
          {isUploading ? `Uploading... ${Math.round(progress)}%` : "Upload Photos"}
        </button>
      )}

      {isUploading && (
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default GalleryUpload;